import { useMemo } from "react";
import { useSEOData } from "./useSEOData";
import { API_BASE_URL } from "../utils/api";

export const useStructuredData = () => {
  const seoData = useSEOData();

  // Schema.org para Organization
  const generateOrganizationSchema = useMemo(() => () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${seoData.siteUrl}#organization`,
    name: seoData.siteName,
    description: seoData.siteDescription,
    url: seoData.siteUrl,
    logo: {
      "@type": "ImageObject",
      url: seoData.siteLogo.startsWith('http') ? seoData.siteLogo : `${seoData.siteUrl}${seoData.siteLogo}`,
      width: 400,
      height: 400,
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: seoData.settings?.contact?.phone || "+57-300-123-4567",
      contactType: "customer service",
      availableLanguage: ["Spanish"],
      areaServed: "CO",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: seoData.settings?.contact?.address || "Arauca, Colombia",
      addressLocality: "Arauca",
      addressRegion: "Arauca",
      postalCode: "810001",
      addressCountry: "CO",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "7.0833",
      longitude: "-70.7625",
    },
    foundingDate: "2010",
    sameAs: [
      seoData.settings?.social?.facebook,
      seoData.settings?.social?.twitter,
      seoData.settings?.social?.instagram,
      seoData.settings?.social?.youtube,
      seoData.settings?.social?.linkedin,
    ].filter(Boolean),
  }), [seoData]);

  // Schema.org para WebSite
  const generateWebSiteSchema = useMemo(() => () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${seoData.siteUrl}#website`,
    name: seoData.siteName,
    alternateName: "Red Arauca",
    description: seoData.siteDescription,
    url: seoData.siteUrl,
    inLanguage: "es-CO",
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${seoData.siteUrl}/buscar?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
    publisher: {
      "@id": `${seoData.siteUrl}#organization`
    },
    copyrightHolder: {
      "@id": `${seoData.siteUrl}#organization`
    },
    copyrightYear: new Date().getFullYear(),
  }), [seoData]);

  // Schema.org para NewsArticle
  const generateArticleSchema = useMemo(() => (post) => {
    if (!post) return null;

    // Procesar imagen del artículo
    const getArticleImage = () => {
      const image = post.featured_image || post.image;
      if (!image) return `${seoData.siteUrl}/assets/default-article-og.jpg`;
      
      if (image.startsWith('http')) return image;
      if (image.startsWith('/uploads/')) return `${API_BASE_URL}${image}`;
      return `${API_BASE_URL}/uploads/${image}`;
    };

    // Procesar tags
    const getTags = () => {
      if (!post.tags) return [];
      if (typeof post.tags === 'string') {
        return post.tags.split(',').map(tag => tag.trim());
      }
      if (Array.isArray(post.tags)) {
        return post.tags;
      }
      return [];
    };

    return {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      "@id": `${seoData.siteUrl}/post/${post.slug}`,
      headline: post.title,
      description: post.excerpt || post.description || post.summary,
      image: {
        "@type": "ImageObject",
        url: getArticleImage(),
        width: 1200,
        height: 630,
      },
      datePublished: post.published_at || post.created_at,
      dateModified: post.updated_at || post.published_at || post.created_at,
      author: {
        "@type": "Person",
        name: post.author || post.author_name || seoData.siteName,
        url: post.author_url || seoData.siteUrl
      },
      publisher: {
        "@id": `${seoData.siteUrl}#organization`
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `${seoData.siteUrl}/post/${post.slug}`,
      },
      articleSection: post.category || post.category_name || "Noticias",
      keywords: getTags().join(", ") || "noticias, arauca, colombia",
      wordCount: post.body ? post.body.replace(/<[^>]*>/g, "").split(" ").length : 0,
      inLanguage: "es-CO",
      isAccessibleForFree: true,
      ...(post.reading_time && { 
        timeRequired: `PT${post.reading_time}M` 
      }),
      about: {
        "@type": "Thing",
        name: post.category || post.category_name || "Noticias",
      }
    };
  }, [seoData]);

  // Schema.org para BreadcrumbList
  const generateBreadcrumbSchema = useMemo(() => (breadcrumbs) => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.name,
        item: crumb.url ? `${seoData.siteUrl}${crumb.url}` : undefined,
      })),
    };
  }, [seoData]);

  // Schema.org para CollectionPage (categorías)
  const generateCollectionPageSchema = useMemo(() => (category, posts = []) => {
    if (!category) return null;

    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "@id": `${seoData.siteUrl}/categoria/${category.slug}`,
      name: `Noticias de ${category.name}`,
      description: category.description || `Todas las noticias sobre ${category.name} en Red Arauca`,
      url: `${seoData.siteUrl}/categoria/${category.slug}`,
      inLanguage: "es-CO",
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: posts.length,
        itemListElement: posts.slice(0, 10).map((post, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "NewsArticle",
            "@id": `${seoData.siteUrl}/post/${post.slug}`,
            headline: post.title,
            url: `${seoData.siteUrl}/post/${post.slug}`,
            datePublished: post.published_at || post.created_at,
            image: post.featured_image || post.image
              ? (post.featured_image || post.image).startsWith('http')
                ? post.featured_image || post.image
                : `${API_BASE_URL}/uploads/${post.featured_image || post.image}`
              : `${seoData.siteUrl}/assets/default-article-og.jpg`,
            author: {
              "@type": "Person",
              name: post.author || post.author_name || seoData.siteName
            }
          },
        })),
      },
      publisher: {
        "@id": `${seoData.siteUrl}#organization`
      },
      about: {
        "@type": "Thing",
        name: category.name,
        description: category.description
      }
    };
  }, [seoData]);

  // Schema.org para FAQ (preguntas frecuentes)
  const generateFAQSchema = useMemo(() => (faqs) => {
    if (!faqs || faqs.length === 0) return null;

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    };
  }, []);

  // Schema.org para LocalBusiness
  const generateLocalBusinessSchema = useMemo(() => () => ({
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `${seoData.siteUrl}#localbusiness`,
    name: seoData.siteName,
    description: seoData.siteDescription,
    url: seoData.siteUrl,
    telephone: seoData.settings?.contact?.phone,
    email: seoData.settings?.contact?.email,
    address: {
      "@type": "PostalAddress",
      streetAddress: seoData.settings?.contact?.address || "Arauca, Colombia",
      addressLocality: "Arauca",
      addressRegion: "Arauca",
      postalCode: "810001",
      addressCountry: "CO",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: "7.0833",
      longitude: "-70.7625",
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "18:00",
    },
    priceRange: "Gratis",
    sameAs: [
      seoData.settings?.social?.facebook,
      seoData.settings?.social?.twitter,
      seoData.settings?.social?.instagram,
    ].filter(Boolean),
  }), [seoData]);

  // Schema.org para SearchResultsPage
  const generateSearchResultsSchema = useMemo(() => (query, results = []) => {
    if (!query) return null;

    return {
      "@context": "https://schema.org",
      "@type": "SearchResultsPage",
      "@id": `${seoData.siteUrl}/buscar?q=${encodeURIComponent(query)}`,
      name: `Resultados de búsqueda para "${query}"`,
      description: `${results.length} resultados encontrados para "${query}" en ${seoData.siteName}`,
      url: `${seoData.siteUrl}/buscar?q=${encodeURIComponent(query)}`,
      inLanguage: "es-CO",
      mainEntity: {
        "@type": "ItemList",
        numberOfItems: results.length,
        itemListElement: results.slice(0, 10).map((result, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: {
            "@type": "NewsArticle",
            headline: result.title,
            url: `${seoData.siteUrl}/post/${result.slug}`,
            datePublished: result.published_at || result.created_at,
          },
        })),
      },
      publisher: {
        "@id": `${seoData.siteUrl}#organization`
      }
    };
  }, [seoData]);

  // Schema.org para WebPage (páginas generales)
  const generateWebPageSchema = useMemo(() => (pageData) => {
    const { title, description, url, breadcrumbs } = pageData;

    return {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": url,
      name: title,
      description: description,
      url: url,
      inLanguage: "es-CO",
      isPartOf: {
        "@id": `${seoData.siteUrl}#website`
      },
      publisher: {
        "@id": `${seoData.siteUrl}#organization`
      },
      ...(breadcrumbs && {
        breadcrumb: {
          "@id": `${url}#breadcrumb`
        }
      })
    };
  }, [seoData]);

  return {
    generateOrganizationSchema,
    generateWebSiteSchema,
    generateArticleSchema,
    generateBreadcrumbSchema,
    generateCollectionPageSchema,
    generateFAQSchema,
    generateLocalBusinessSchema,
    generateSearchResultsSchema,
    generateWebPageSchema,
  };
};